import { useEffect } from 'react';
import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/context/AuthContext';
import { ADMIN_NAV } from '@/constants/nav';
import { getIcon } from '@/lib/icons';
import { LogOut, Menu, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import useSEO from '@/hooks/useSEO';

const AdminSidebar = ({ onNavigate }) => {
    const location = useLocation();
    return (
        <nav className="flex flex-col gap-0.5 p-3" data-testid="admin-sidebar">
            {ADMIN_NAV.map((item) => {
                const Icon = getIcon(item.icon);
                const active =
                    item.to === '/admin'
                        ? location.pathname === '/admin'
                        : location.pathname.startsWith(item.to);
                return (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={onNavigate}
                        end={item.to === '/admin'}
                        className={cn(
                            'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium',
                            active
                                ? 'bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))]'
                                : 'text-foreground/75 hover:bg-muted'
                        )}
                        data-testid={`admin-nav-${item.to.replace('/admin', '').replace('/', '') || 'dashboard'}`}
                    >
                        <Icon className="h-4 w-4" />
                        {item.label}
                    </NavLink>
                );
            })}
        </nav>
    );
};

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Block search-engine indexing across the entire admin surface.
    useSEO({
        title: 'Admin Console',
        description: 'VVVSI Trust admin console.',
        noindex: true,
    });

    useEffect(() => {
        document.body.classList.add('admin-scope');
        return () => document.body.classList.remove('admin-scope');
    }, []);

    return (
        <div className="admin-scope min-h-screen bg-background flex">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex flex-col w-[260px] shrink-0 border-r border-border bg-card">
                <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border">
                    <div className="h-9 w-9 rounded-md bg-[hsl(var(--primary))] text-primary-foreground flex items-center justify-center font-display font-bold">
                        V
                    </div>
                    <div>
                        <div className="font-display font-semibold text-sm text-[hsl(var(--primary))]">
                            VVVSI Admin
                        </div>
                        <div className="text-[10px] text-muted-foreground">CMS Console</div>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <AdminSidebar />
                </div>
                <div className="p-3 border-t border-border">
                    <div className="px-3 py-2 rounded-lg bg-muted mb-2">
                        <div className="text-xs text-muted-foreground">Signed in as</div>
                        <div className="text-sm font-medium truncate">{user?.email}</div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            logout();
                            navigate('/admin/login');
                        }}
                        className="w-full"
                        data-testid="admin-logout-button"
                    >
                        <LogOut className="h-4 w-4 mr-1.5" /> Log out
                    </Button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0">
                <header
                    className="h-16 border-b border-border bg-card flex items-center gap-3 px-4 sm:px-6"
                    data-testid="admin-topbar"
                >
                    {/* Mobile toggle */}
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden"
                                data-testid="admin-mobile-menu-button"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-[280px] p-0">
                            <SheetTitle className="sr-only">Admin navigation</SheetTitle>
                            <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border">
                                <div className="h-9 w-9 rounded-md bg-[hsl(var(--primary))] text-primary-foreground flex items-center justify-center font-display font-bold">
                                    V
                                </div>
                                <div>
                                    <div className="font-display font-semibold text-sm text-[hsl(var(--primary))]">
                                        VVVSI Admin
                                    </div>
                                </div>
                            </div>
                            <AdminSidebar />
                        </SheetContent>
                    </Sheet>
                    <div className="font-display font-semibold text-lg text-[hsl(var(--primary))] hidden sm:block">
                        Content Management
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <a
                            href="/"
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid="admin-view-site-link"
                        >
                            <Button size="sm" variant="outline">
                                <ExternalLink className="h-4 w-4 mr-1.5" /> View Site
                            </Button>
                        </a>
                    </div>
                </header>
                <main className="p-4 sm:p-6 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
